/**
 * A monotonic clock that advances only while the document is visible.
 *
 * `performance.now()` keeps advancing while a tab is hidden. That is normally useful,
 * but it makes user-facing duration vitals report minutes when a background tab takes
 * about a second to render after activation. This clock removes those hidden intervals
 * while keeping all timing arithmetic in the same performance-time domain.
 */
export class ForegroundClock {
    private completedHiddenMs = 0;
    private hiddenStartedPerfNow: number | undefined;

    constructor(initiallyHidden: boolean) {
        // The client instrumentation module loads at document startup. If the document
        // starts hidden (background-open/prerender), the hidden interval began at the
        // performance time origin rather than when this module happened to evaluate.
        if (initiallyHidden) this.hiddenStartedPerfNow = 0;
    }

    setHidden(hidden: boolean, perfNow: number): void {
        if (hidden) {
            if (this.hiddenStartedPerfNow === undefined) this.hiddenStartedPerfNow = perfNow;
            return;
        }

        if (this.hiddenStartedPerfNow !== undefined) {
            this.completedHiddenMs += Math.max(0, perfNow - this.hiddenStartedPerfNow);
            this.hiddenStartedPerfNow = undefined;
        }
    }

    at(perfNow: number): number {
        const currentHiddenMs =
            this.hiddenStartedPerfNow === undefined ? 0 : Math.max(0, perfNow - this.hiddenStartedPerfNow);
        return Math.max(0, perfNow - this.completedHiddenMs - currentHiddenMs);
    }
}

export interface ForegroundTimingStart {
    perfNow: number;
    foregroundPerfNow: number;
}

export interface ForegroundDuration {
    /** Start adjusted forward so `startPerfNow + durationMs` still equals the real end. */
    startPerfNow: number;
    durationMs: number;
    hiddenDurationMs: number;
}

const clock = new ForegroundClock(typeof document !== 'undefined' && document.visibilityState !== 'visible');

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        clock.setHidden(document.visibilityState !== 'visible', performance.now());
    });
}

export function getForegroundPerfNow(perfNow = performance.now()): number {
    return clock.at(perfNow);
}

export function captureForegroundTimingStart(perfNow = performance.now()): ForegroundTimingStart {
    return { perfNow, foregroundPerfNow: getForegroundPerfNow(perfNow) };
}

export function completeForegroundDuration(
    start: ForegroundTimingStart,
    endPerfNow = performance.now(),
    endForegroundPerfNow = getForegroundPerfNow(endPerfNow),
): ForegroundDuration {
    const wallDurationMs = Math.max(0, endPerfNow - start.perfNow);
    const foregroundDurationMs = Math.min(wallDurationMs, Math.max(0, endForegroundPerfNow - start.foregroundPerfNow));
    const durationMs = Math.round(foregroundDurationMs);

    return {
        startPerfNow: endPerfNow - durationMs,
        durationMs,
        hiddenDurationMs: Math.max(0, Math.round(wallDurationMs - foregroundDurationMs)),
    };
}
